import { useEffect, useMemo, useState } from 'react';
import { getActivity } from '../../utils/company';
import { errorBox } from '../AuthLayout';
import { CardStatus } from './bits';

const COLUMNS = [
  { id: 'full_name', label: 'Person' },
  { id: 'views', label: 'Views', numeric: true },
  { id: 'saved', label: 'Contacts saved', numeric: true },
  { id: 'leads', label: 'Leads', numeric: true },
];
const RANGES = [7, 30, 90];

/** Company → Activity: views, contacts saved and leads per card (company_activity RPC). */
export default function Activity({ org }) {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ by: 'views', dir: 'desc' });

  useEffect(() => {
    let active = true;
    setRows(null);
    setError('');
    getActivity(org.id, days)
      // "Contacts saved" counts direct downloads plus downloads after sharing details (as on My cards)
      .then((data) => active && setRows(data.map((r) => ({ ...r, views: Number(r.views), saves: Number(r.saves), leads: Number(r.leads), saved: Number(r.saves) + Number(r.leads) }))))
      .catch((e) => active && setError(e.message || 'Could not load activity.'));
    return () => { active = false; };
  }, [org.id, days]);

  const sorted = useMemo(() => {
    if (!rows) return rows;
    const col = COLUMNS.find((c) => c.id === sort.by);
    const mul = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => (col.numeric
      ? (a[sort.by] - b[sort.by]) * mul || a.full_name.localeCompare(b.full_name)
      : a.full_name.localeCompare(b.full_name) * mul));
  }, [rows, sort]);

  const totals = useMemo(() => (rows || []).reduce((t, r) => ({
    views: t.views + r.views, saved: t.saved + r.saved, leads: t.leads + r.leads,
  }), { views: 0, saved: 0, leads: 0 }), [rows]);

  const sortBy = (id) => setSort((s) => (s.by === id
    ? { by: id, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { by: id, dir: id === 'full_name' ? 'asc' : 'desc' }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <h2 className="text-[22px] font-bold flex-1 min-w-[200px]">Last {days} days</h2>
        <div role="group" aria-label="Time range" className="inline-flex p-1 rounded-xl bg-white border border-line">
          {RANGES.map((d) => (
            <button key={d} onClick={() => setDays(d)} aria-pressed={days === d}
              className={`h-9 px-3.5 rounded-lg text-sm ${days === d ? 'bg-blue-soft text-blue font-semibold' : 'text-ink-muted font-medium hover:text-ink'}`}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      {error && <div className={errorBox}>{error}</div>}

      <div className="grid grid-cols-3 gap-3">
        {[['Views', totals.views], ['Contacts saved', totals.saved], ['Leads', totals.leads]].map(([label, value]) => (
          <div key={label} className="bg-white rounded-2xl border border-line p-4 sm:p-5 min-w-0">
            <span className="block text-xs font-semibold tracking-wider uppercase text-ink-muted truncate">{label}</span>
            <span className="block font-display font-bold text-[26px] sm:text-[32px] leading-9 mt-1">{rows ? value.toLocaleString() : '…'}</span>
          </div>
        ))}
      </div>

      {sorted === null && !error && <p className="text-ink-muted">Loading activity...</p>}
      {sorted?.length === 0 && (
        <p className="bg-white rounded-2xl border border-dashed border-line-strong px-5 py-8 text-center text-ink-muted">
          No cards in {org.name} yet. Invite people from the People tab.
        </p>
      )}

      {sorted?.length > 0 && (
        <>
          <div className="lg:hidden flex items-center gap-2 text-sm">
            <label htmlFor="activity-sort" className="text-ink-muted">Sort by</label>
            <select id="activity-sort" value={`${sort.by}:${sort.dir}`} onChange={(e) => { const [by, dir] = e.target.value.split(':'); setSort({ by, dir }); }}
              className="h-9 px-2.5 border border-line-strong rounded-lg text-sm bg-white">
              <option value="views:desc">Most views</option>
              <option value="saved:desc">Most contacts saved</option>
              <option value="leads:desc">Most leads</option>
              <option value="full_name:asc">Name A–Z</option>
            </select>
          </div>
          <ul className="grid grid-cols-1 gap-3 lg:hidden">
            {sorted.map((r) => (
              <li key={r.card_id} className="bg-white rounded-2xl border border-line p-4 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <PersonCell r={r} />
                  <CardStatus status={r.card_status} />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-line">
                  {[['Views', r.views], ['Saved', r.saved], ['Leads', r.leads]].map(([l, v]) => (
                    <div key={l}>
                      <span className="block text-xs font-semibold tracking-wider uppercase text-ink-muted">{l}</span>
                      <span className="font-display font-bold text-xl tabular-nums">{v}</span>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden lg:block bg-white rounded-2xl border border-line overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left">
                <tr className="text-xs font-semibold tracking-wider uppercase text-ink-muted">
                  {COLUMNS.map((c) => (
                    <th key={c.id} className={`px-4 py-3 ${c.numeric ? 'text-right' : ''}`}
                      aria-sort={sort.by === c.id ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button onClick={() => sortBy(c.id)} className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold hover:text-ink ${sort.by === c.id ? 'text-ink' : ''}`}>
                        {c.label}
                        <span aria-hidden="true" className={sort.by === c.id ? 'text-blue' : 'text-line-strong'}>
                          {sort.by === c.id ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {sorted.map((r) => (
                  <tr key={r.card_id}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3"><PersonCell r={r} />{r.card_status !== 'active' && <CardStatus status={r.card_status} />}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.views.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.saved.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.leads.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-surface border-t border-line font-semibold text-ink">
                <tr>
                  <td className="px-4 py-3">Total · {sorted.length} {sorted.length === 1 ? 'card' : 'cards'}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{totals.views.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{totals.saved.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{totals.leads.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function PersonCell({ r }) {
  return (
    <div className="min-w-0">
      <div className="font-semibold text-ink truncate">{r.full_name}</div>
      {r.email && <div className="text-ink-muted text-sm truncate">{r.email}</div>}
    </div>
  );
}
